import chalk from 'chalk';
import ora from 'ora';
import { requireTeam } from '../lib/auth-guard.js';
import { api, getHostId } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

// ---------------------------------------------------------------------------
// agt host list
// ---------------------------------------------------------------------------

export async function hostListCommand(): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching hosts\u2026', isSilent: json });
  spinner.start();

  try {
    const data = await api.get<{
      hosts: Array<{
        name: string;
        status: string;
        description: string | null;
        framework: string;
        agents: number;
        last_seen_at: string | null;
        key_prefix: string | null;
        created_at: string;
      }>;
    }>('/hosts');

    spinner.stop();

    if (!data.hosts || data.hosts.length === 0) {
      if (json) {
        jsonOutput({ ok: true, hosts: [] });
      } else {
        info('No hosts found. Create one with `agt host create --name <name>`.');
      }
      return;
    }

    if (json) {
      jsonOutput({ ok: true, hosts: data.hosts });
      return;
    }

    const rows = data.hosts.map((h) => {
      const status = h.status === 'active'
        ? chalk.green('active')
        : chalk.red('decommissioned');

      const agents = String(h.agents);

      const lastSeen = h.last_seen_at
        ? new Date(h.last_seen_at).toLocaleDateString()
        : chalk.dim('never');

      const prefix = h.key_prefix
        ? `tlk_${h.key_prefix}\u2026`
        : chalk.dim('none');

      return [h.name, status, agents, lastSeen, prefix];
    });

    table(['Name', 'Status', 'Agents', 'Last Seen', 'Key'], rows);
  } catch (err) {
    spinner.fail('Failed to fetch hosts.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host assign
// ---------------------------------------------------------------------------

interface HostAssignOptions {
  force?: boolean;
}

export async function hostAssignCommand(
  hostName: string,
  agentCodeNames: string[],
  opts: HostAssignOptions,
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();

  if (!agentCodeNames || agentCodeNames.length === 0) {
    if (json) {
      jsonOutput({ ok: false, error: 'At least one agent code name is required' });
    } else {
      error('At least one agent code name is required.');
    }
    process.exitCode = 1;
    return;
  }

  const spinner = ora({ text: 'Assigning agents\u2026', isSilent: json });
  spinner.start();

  try {
    await api.post(`/hosts/${encodeURIComponent(hostName)}/assign`, {
      agents: agentCodeNames,
      force: opts.force,
    });

    spinner.succeed(`Agents assigned to ${chalk.bold(hostName)}.`);

    if (json) {
      jsonOutput({ ok: true, host: hostName, assigned: agentCodeNames });
      return;
    }

    for (const name of agentCodeNames) {
      info(`  ${name}`);
    }
  } catch (err) {
    spinner.fail('Failed to assign agents.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host unassign
// ---------------------------------------------------------------------------

export async function hostUnassignCommand(
  hostName: string,
  agentCodeNames: string[],
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();

  if (!agentCodeNames || agentCodeNames.length === 0) {
    if (json) {
      jsonOutput({ ok: false, error: 'At least one agent code name is required' });
    } else {
      error('At least one agent code name is required.');
    }
    process.exitCode = 1;
    return;
  }

  const spinner = ora({ text: 'Unassigning agents\u2026', isSilent: json });
  spinner.start();

  try {
    await api.post(`/hosts/${encodeURIComponent(hostName)}/unassign`, {
      agents: agentCodeNames,
    });

    spinner.succeed(`Agents unassigned from ${chalk.bold(hostName)}.`);

    if (json) {
      jsonOutput({ ok: true, host: hostName, unassigned: agentCodeNames });
      return;
    }

    for (const name of agentCodeNames) {
      info(`  ${name}`);
    }
  } catch (err) {
    spinner.fail('Failed to unassign agents.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host agents [host-name]
// ---------------------------------------------------------------------------

export async function hostAgentsCommand(hostName?: string): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching host agents\u2026', isSilent: json });
  spinner.start();

  try {
    // If no host name given, use the API key's host identity via /host/agents
    const hostId = await getHostId();

    if (!hostName && !hostId) {
      spinner.fail('No host specified.');
      if (json) {
        jsonOutput({ ok: false, error: 'Provide a host name or use an AGT_API_KEY' });
      } else {
        error('Provide a host name, or set AGT_API_KEY to auto-resolve the host.');
      }
      process.exitCode = 1;
      return;
    }

    let agents: Array<{
      code_name: string;
      display_name: string;
      status: string;
      environment: string;
      risk_tier?: string;
      assigned_at?: string;
    }>;

    if (hostId && !hostName) {
      // Host runtime path — resolve from API key
      const data = await api.post<{
        agents: typeof agents;
      }>('/host/agents', { host_id: hostId });
      agents = data.agents ?? [];
    } else {
      // Dashboard path — look up by name
      const teamSlug = requireTeam();
      if (!teamSlug) return;

      const data = await api.get<{
        agents: typeof agents;
      }>(`/hosts/${encodeURIComponent(hostName!)}/agents`);
      agents = data.agents ?? [];
    }

    spinner.stop();

    if (agents.length === 0) {
      if (json) {
        jsonOutput({ ok: true, agents: [] });
      } else {
        info('No agents assigned to this host.');
        info('Assign agents with `agt host assign <host-name> <agent-code-name>`.');
      }
      return;
    }

    if (json) {
      jsonOutput({ ok: true, agents });
      return;
    }

    const rows = agents.map((a) => {
      const statusColor = a.status === 'active' ? chalk.green : chalk.yellow;
      return [
        a.code_name,
        a.display_name,
        statusColor(a.status),
        a.environment,
      ];
    });

    table(['Code Name', 'Display Name', 'Status', 'Environment'], rows);
  } catch (err) {
    spinner.fail('Failed to fetch host agents.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host rotate-key
// ---------------------------------------------------------------------------

export async function hostRotateKeyCommand(hostName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: 'Rotating host key\u2026', isSilent: json });
  spinner.start();

  try {
    const data = await api.post<{
      ok: boolean;
      host: string;
      key: { prefix: string; raw_key: string };
    }>(`/hosts/${encodeURIComponent(hostName)}/rotate-key`);

    spinner.succeed(`Key rotated for ${chalk.bold(hostName)}.`);

    if (json) {
      jsonOutput({
        ok: true,
        host: hostName,
        key: data.key,
      });
      return;
    }

    console.log();
    info(`Host:   ${chalk.bold(hostName)}`);
    info(`Prefix: ${data.key.prefix}`);
    console.log();
    console.log(chalk.yellow.bold('  Save this key now \u2014 it will not be shown again:'));
    console.log();
    console.log(`  ${chalk.green.bold(data.key.raw_key)}`);
    console.log();
  } catch (err) {
    spinner.fail('Failed to rotate key.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host maintenance-window
// ---------------------------------------------------------------------------

interface HostMaintenanceWindowOptions {
  start?: string;
  end?: string;
  tz?: string;
  clear?: boolean;
}

interface MaintenanceWindowResponse {
  override: {
    maintenance_window_start: string | null;
    maintenance_window_end: string | null;
    maintenance_timezone: string | null;
  };
  effective: { start: string; end: string; timezone: string };
}

export async function hostMaintenanceWindowCommand(
  hostName: string,
  opts: HostMaintenanceWindowOptions,
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const path = `/hosts/${encodeURIComponent(hostName)}/maintenance-window`;
  const setting =
    opts.clear || opts.start !== undefined || opts.end !== undefined || opts.tz !== undefined;

  // Show mode — no set flags.
  if (!setting) {
    const spinner = ora({ text: 'Fetching maintenance window…', isSilent: json });
    spinner.start();
    try {
      const data = await api.get<MaintenanceWindowResponse>(path);
      spinner.stop();
      if (json) {
        jsonOutput({ ok: true, ...data });
        return;
      }
      const ov = data.override;
      const hasOverride =
        ov.maintenance_window_start !== null ||
        ov.maintenance_window_end !== null ||
        ov.maintenance_timezone !== null;
      info(`Host:      ${chalk.bold(hostName)}`);
      info(
        `Effective: ${chalk.green(`${data.effective.start}–${data.effective.end}`)} ${data.effective.timezone}`,
      );
      info(
        `Override:  ${hasOverride ? chalk.cyan('set') : chalk.dim('none (default 01:00–02:00)')}`,
      );
      info('Set with --start/--end/--tz, or --clear to follow the default.');
    } catch (err) {
      spinner.fail('Failed to fetch maintenance window.');
      if (json) jsonOutput({ ok: false, error: (err as Error).message });
      else error((err as Error).message);
      process.exitCode = 1;
    }
    return;
  }

  // Set/clear mode. Require both --start and --end together (the window needs
  // both bounds; clearing one without the other is ambiguous).
  // --clear is exclusive: combining it with set flags is contradictory, and
  // silently honouring --clear while ignoring --start/--end/--tz would do the
  // opposite of what the operator likely intended. Fail fast.
  if (opts.clear && (opts.start !== undefined || opts.end !== undefined || opts.tz !== undefined)) {
    const msg = 'Use --clear on its own — it cannot be combined with --start/--end/--tz.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }
  if (!opts.clear && (opts.start === undefined) !== (opts.end === undefined)) {
    const msg = 'Provide both --start and --end (or use --clear).';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }
  // A timezone without time bounds is meaningless — reject --tz on its own.
  if (!opts.clear && opts.tz !== undefined && opts.start === undefined) {
    const msg = 'Provide --start and --end when setting --tz (or use --clear).';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  // When setting times without an explicit --tz, preserve the host's existing
  // timezone override rather than silently clearing it (the API replaces all
  // three fields, so we must send the current tz to keep it).
  let resolvedTz: string | null = opts.tz ?? null;
  if (!opts.clear && opts.tz === undefined) {
    try {
      const cur = await api.get<MaintenanceWindowResponse>(path);
      resolvedTz = cur.override.maintenance_timezone;
    } catch (err) {
      const spinner0 = ora({ isSilent: json });
      spinner0.fail('Failed to read current maintenance window.');
      if (json) jsonOutput({ ok: false, error: (err as Error).message });
      else error((err as Error).message);
      process.exitCode = 1;
      return;
    }
  }

  const payload = opts.clear
    ? { maintenance_window_start: null, maintenance_window_end: null, maintenance_timezone: null }
    : {
        maintenance_window_start: opts.start ?? null,
        maintenance_window_end: opts.end ?? null,
        maintenance_timezone: resolvedTz,
      };

  const spinner = ora({ text: 'Updating maintenance window…', isSilent: json });
  spinner.start();
  try {
    const data = await api.post<{
      maintenance_window: { start: string | null; end: string | null; timezone: string | null };
      note?: string;
    }>(path, payload);
    spinner.succeed(`Maintenance window updated for ${chalk.bold(hostName)}.`);
    if (json) {
      jsonOutput({ ok: true, ...data });
      return;
    }
    if (data.note) info(data.note);
  } catch (err) {
    spinner.fail('Failed to update maintenance window.');
    if (json) jsonOutput({ ok: false, error: (err as Error).message });
    else error((err as Error).message);
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt host decommission
// ---------------------------------------------------------------------------

export async function hostDecommissionCommand(hostName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: 'Decommissioning host\u2026', isSilent: json });
  spinner.start();

  try {
    await api.post(`/hosts/${encodeURIComponent(hostName)}/decommission`);

    spinner.succeed(`Host "${chalk.bold(hostName)}" decommissioned.`);

    if (json) {
      jsonOutput({
        ok: true,
        host: hostName,
        status: 'decommissioned',
      });
      return;
    }

    info(`Host:   ${hostName}`);
    info(`Status: ${chalk.red('decommissioned')}`);
    info('API key revoked. Agents remain assigned for audit visibility.');
    info('Reassign agents to another host with `agt host assign --force`.');
  } catch (err) {
    spinner.fail('Failed to decommission host.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
